"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Brain,
  Sparkles,
  Target,
  Stethoscope,
  ShieldCheck,
  Activity,
  Layers,
  CheckCircle2,
  ArrowRight,
  Crown,
  Mic,
  Award,
  Compass,
  TrendingUp,
  Briefcase,
  Building2,
  Flag,
  Heart,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { BentoBlock } from "@/lib/blocks/bento-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const COMPONENTS = [
  {
    num: "01",
    title: "Protocols",
    body: "Documented sequences that produce predictable outcomes. Without protocols, outcomes depend on individual judgment. With protocols, outcomes depend on system design.",
    accent: "red",
  },
  {
    num: "02",
    title: "Checklists",
    body: "The discipline that prevents omission errors. In sovereign-scale operations where the margin for error is zero, checklists ensure no critical step is missed.",
    accent: "purple",
  },
  {
    num: "03",
    title: "Reviews",
    body: "Structured processes for extracting learning from outcomes. The Morbidity and Mortality conference adapted to organizations — the ritual where leaders own what went wrong.",
    accent: "cyan",
  },
  {
    num: "04",
    title: "Outcome Accountability",
    body: "Every metric is an outcome metric. The organization does not report how hard it worked. It reports what it delivered. Results, not effort.",
    accent: "emerald",
  },
];

const PRINCIPLES = [
  { num: "01", title: "Diagnose Before Treating", body: "The symptom is never the disease. Most organizations act on symptoms. The disease progresses undetected. The method demands forensic diagnosis before any intervention." },
  { num: "02", title: "Triage Under Pressure", body: "When everything is urgent, nothing is solved well. The leaders who fail rarely lack the ability to act. They lack the ability to rank." },
  { num: "03", title: "Run the Protocol; Keep the Judgment", body: "Protocols carry the ninety-five percent. Judgment handles the five percent that do not. The mature operator loves the protocol and knows when to put it down." },
  { num: "04", title: "Own the Outcome, Not the Effort", body: "Medicine does not credit a physician for how hard the attempt was. The patient lived or did not. Organizations must be measured the same way." },
  { num: "05", title: "First, Do No Harm", body: "Under pressure, most leaders make the situation worse. The discipline of not making it worse is the most underrated operational skill in leadership." },
  { num: "06", title: "Hire the Talent Others Overlook", body: "The most reliable competitive advantage available is the one the world is mis-pricing. Optimize for the right signals and the talent emerges." },
];

const METHODOLOGY = [
  { num: "01", title: "Intelligence", body: "Every engagement begins with intelligence, not assumptions. The ground truth is discovered before any strategy is formed.", accent: "red" },
  { num: "02", title: "Analysis", body: "Pattern identification, anomaly detection, correlation mapping. The symptom is never the disease. The analysis is forensic, not descriptive.", accent: "purple" },
  { num: "03", title: "Strategy", body: "The architecture of victory. The Penta P framework deployed. Each platform assigned its role. Each team assigned its mission.", accent: "cyan" },
  { num: "04", title: "Orchestration", body: "All platforms coordinated simultaneously. Decision time compressed from days to under one hour. Ninety-five percent coordination success.", accent: "emerald" },
  { num: "05", title: "Amplification", body: "Content deploys at scale with platform-specific optimization. The signal becomes louder than the noise. Reach, resonance, results.", accent: "amber-gold" },
  { num: "06", title: "Feedback", body: "Continuous monitoring and outcome measurement flow back into intelligence. Every engagement makes the next one smarter.", accent: "denim" },
];

const EVIDENCE = [
  { metric: "83.3", suffix: "%", label: "Campaign Success", sub: "15 victories / 18 engagements", accent: "red" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 seconds max/year", accent: "purple" },
  { metric: "0", suffix: "", label: "Security Breaches", sub: "15+ years clean record", accent: "emerald" },
  { metric: "900", suffix: "M+", label: "Voters Reached", sub: "Continental reach", accent: "amber-gold" },
];

const IMPLEMENTATION = [
  { num: "01", title: "Document the Operating Steps", body: "If the operating steps cannot be written down, the outcome is luck. Every critical process must be documented as a protocol.", accent: "red" },
  { num: "02", title: "Build the Checklists", body: "The discipline that prevents omission errors. The most complex professionals produce better outcomes with simple checklists.", accent: "purple" },
  { num: "03", title: "Institutionalize the Reviews", body: "The Morbidity and Mortality conference adapted to organizations. Every engagement feeds into the review process.", accent: "cyan" },
  { num: "04", title: "Restore Outcome Accountability", body: "Measure by what was delivered, not by how hard it was tried. Effort is courtesy. Outcomes are the measure.", accent: "emerald" },
  { num: "05", title: "Hire for the Right Signals", body: "Optimize for rigor, communication, risk discipline, retention, and collaboration. The talent that emerges is the talent that outperforms.", accent: "amber-gold" },
];

export default function OperationalExcellencePage() {
  return (
    <>
      <PageHero
        icon={Brain}
        overline="Operational Excellence"
        overlineClassName="text-red"
        title={
          <>
            The Engineered System That <span className="text-gradient-premium">Produces Verified Outcomes.</span>
          </>
        }
        subtitle="Excellence is not a value. It is a system. The distinction is not semantic — it is the difference between organizations that achieve outcomes by design and organizations that achieve them by accident. The operational architecture that Dr. Jyoti Kush commands produces results no conventional organization can replicate."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Operational Excellence" }]}
        badge="Engineered, not aspirational"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
              <Sparkles className="size-3.5 text-red" />
              <span className="text-overline text-red">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Excellence is a system, not a value.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              When excellence is a value — something an organization aspires to — it becomes subject to inconsistency, interpretation, and erosion. When excellence is a system — a set of protocols, checklists, reviews, and accountability mechanisms — it becomes independent of any individual. The system produces outcomes regardless of who is operating it.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The operational architecture that Dr. Jyoti Kush commands — built in-house over fifteen-plus years, deployed across eighteen countries, refined through every global disruption — produces verified outcomes not because every individual involved is exceptional, but because the system is engineered to produce excellence regardless of individual variation.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The 83.3% campaign success rate. The fifteen victories in eighteen engagements. The zero security breaches. These are not the products of heroics. They are the products of a system that makes excellence the default outcome rather than the exceptional one.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Four Components</span>
          </div>
          <h2 className="text-display-md mb-4">The architecture of engineered excellence.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four components address specific failure modes. Together, they produce an architecture where excellence is the default outcome rather than the exceptional one.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {COMPONENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{c.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{c.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Six Clinical Principles</span>
          </div>
          <h2 className="text-display-md mb-4">The operating method, forged in a delivery room.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six principles. Each addresses a specific failure mode. Together, they constitute the doctrine that governs every engagement — proven across eighteen countries, applied to sovereign-scale operations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRINCIPLES.map((p, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="text-display-sm font-bold opacity-30 leading-none mb-3" style={{ color: 'currentColor' }}>
                  <span className={a.text}>{p.num}</span>
                </div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Target className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Six-Stage Methodology</span>
          </div>
          <h2 className="text-display-md mb-4">The battle-tested operating system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every engagement follows this sequence. The stages are sequential in logic but iterative in practice. The loop never closes — it accelerates.
          </p>
        </div>
        <div className="space-y-3">
          {METHODOLOGY.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring}`}>
                    <span className="text-body-md font-bold text-white">{m.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{m.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Practical Implementation</span>
          </div>
          <h2 className="text-display-md mb-4">From thesis to architecture.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The transition from excellence-as-aspiration to excellence-as-system requires deliberate architectural choices. Five steps that move any organization from values to infrastructure.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {IMPLEMENTATION.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{s.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Award className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Why It Works</span>
          </div>
          <h2 className="text-display-md mb-4">The clinical discipline that scales.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Medicine is the only profession that has systematized excellence to the point of making it predictable. The parallel is not metaphorical. It is structural — and it is the foundation of the operational excellence methodology.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Stethoscope, title: "Evidence Over Intuition", body: "Every operational decision grounded in data. The intelligence pipeline filters millions of data points to the less than 0.01% that represent actionable insight." },
            { icon: ShieldCheck, title: "Protocols That Scale", body: "A documented sequence of steps produces predictable outcomes. The system is the genius, not the individual. Outcomes do not depend on who operates it." },
            { icon: Compass, title: "Judgment That Adapts", body: "The mature operator loves the protocol and trusts it — and knows exactly when to put it down. Five percent of cases require judgment. The operator keeps it." },
          ].map((b, i) => {
            const accents = ["red", "purple", "cyan"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg">
                  <b.icon className="size-6 text-white" />
                </div>
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{b.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{b.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Layers className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Operational Crisis</span>
          </div>
          <h2 className="text-display-md mb-4">The execution gap the world ignores.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Organizations that treat excellence as a value achieve it only sometimes. The thesis is built on the failures the world refuses to name — and the structural remedies that close them.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { title: "Excellence Treated as a Value", body: "Values are aspirations. Aspirations erode under pressure. Organizations that print excellence on walls produce it only when conditions are favorable — and lose it the moment conditions change.", accent: "red" },
            { title: "Talent Systematically Misallocated", body: "The world selects on the wrong signals — confidence mistaken for competence, volume mistaken for capability, self-promotion mistaken for results. The most reliable pool is the one the market is mis-pricing.", accent: "purple" },
            { title: "Execution Gaps at the Top", body: "Organizations fail not because they lack vision but because they lack operators who can convert vision into verified outcomes. The strategist and the operator are different people — the world rewards the strategist.", accent: "cyan" },
            { title: "Consequence at Sovereign Scale", body: "When the client is a government, royal household, or political leader facing existential contest, an operational failure is not a missed quarterly target. It is a national consequence.", accent: "emerald" },
            { title: "The Security Vacuum", body: "Digital infrastructure underpins every critical function — elections, defense, finance. Reliance on third-party tools, licensed platforms, and generic solutions is not a gap. It is a liability.", accent: "amber-gold" },
            { title: "Activity Replaces Outcome", body: "Effort is credited, intention is praised, hours are rewarded — and outcomes are excused. The restoration of outcome accountability is the first move toward operational excellence.", accent: "denim" },
          ].map((b, i) => {
            const a = ACCENT[b.accent];
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{b.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{b.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20" id="five-step-process" ariaLabel="Five-Step Process Map">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Target className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Five-Step Process Map</span>
          </div>
          <h2 className="text-display-md mb-4">The signature operational architecture.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five operational satellites. One unifying center. The architecture that has produced eighteen-country execution with zero breach record — codified once, replicated everywhere.
          </p>
        </div>
        <ArchitectureDiagramBlock
          center={{
            label: "Operational Excellence",
            body: "The single engine. Excellence, designed as system, measured as outcome, deployed as doctrine.",
            icon: <Stethoscope className="size-5" />,
          }}
          satellites={[
            { label: "Diagnose", body: "Pattern recognition, anomaly detection, root cause. Forensic before interventional.", icon: <Activity className="size-4" />, accent: "red" },
            { label: "Triage", body: "Sequence under fear. What is fatal now. What can wait. What must be monitored.", icon: <Target className="size-4" />, accent: "purple" },
            { label: "Protocol", body: "Documented sequences that produce predictable outcomes across ninety-five percent of cases.", icon: <Layers className="size-4" />, accent: "cyan" },
            { label: "Outcome", body: "Every metric is an outcome metric. The system is credited for results, never for effort.", icon: <CheckCircle2 className="size-4" />, accent: "emerald" },
            { label: "Do No Harm", body: "Stabilize before amplifying. Contain before responding. Protect before defending.", icon: <ShieldCheck className="size-4" />, accent: "amber-gold" },
          ]}
          connections={[
            { from: -1, to: 0 }, { from: -1, to: 1 }, { from: -1, to: 2 }, { from: -1, to: 3 }, { from: -1, to: 4 },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Building2 className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Platform Ecosystem</span>
          </div>
          <h2 className="text-display-md mb-4">Nine platforms. One operational fabric.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Built in-house over fifteen-plus years. None can be purchased, licensed, or replicated without the same decade of investment. The integration is the moat. The orchestration is the advantage.
          </p>
        </div>
        <BentoBlock
          items={[
            { title: "Neural Command Interface", body: "Unifies all nine platforms into one operational fabric. Decision time compressed from twenty-four to seventy-two hours down to under one hour. Ninety-five percent coordination success.", icon: <Activity className="size-5" />, accent: "red", span: "lg" },
            { title: "AI-Driven Digital Intelligence", body: "Monitors two hundred-plus platforms, one hundred thousand-plus news sources, one thousand-plus dark-web sources across fifty-plus languages. Eighty-nine percent crisis prediction accuracy.", icon: <Sparkles className="size-5" />, accent: "purple", span: "sm" },
            { title: "Autonomous Perception", body: "Narrative engineering at scale. Ninety-five to ninety-eight percent organic authenticity. Three hundred to five hundred percent narrative penetration improvement.", icon: <Target className="size-5" />, accent: "cyan", span: "sm" },
            { title: "Content Domination Network", body: "Media syndication across one thousand-plus syndication points. Three hundred forty percent average reach increase. Seventeen platforms in under five minutes.", icon: <TrendingUp className="size-5" />, accent: "emerald", span: "sm" },
            { title: "Ground-Truth Intelligence", body: "Booth-level voter prediction accuracy of eighty-seven percent — twenty-six points higher than digital-only. Fifteen victories in eighteen engagements.", icon: <Compass className="size-5" />, accent: "amber-gold", span: "sm" },
            { title: "Unified Governance Neural Hub", body: "Five pillars — Policy, Police, Pulse, Power, Public. Deployed at two hundred-plus organizations including fifty-plus Fortune 500 companies. Federal ROI: six hundred twenty percent.", icon: <Building2 className="size-5" />, accent: "denim", span: "wide" },
            { title: "Crisis Transformation Engine", body: "Three hundred eighty-four to one thousand four hundred sixteen times faster than traditional approaches. Eighty-five to ninety-five percent crisis success rate. Sub-five-minute response.", icon: <ShieldCheck className="size-5" />, accent: "coral", span: "sm" },
            { title: "Sovereign Security System", body: "Military-grade encryption, zero-trust, post-quantum cryptography. Ninety-nine point nine nine nine nine percent uptime. Zero incidents across all deployments.", icon: <ShieldCheck className="size-5" />, accent: "lime", span: "sm" },
            { title: "Governance Cognition Matrix", body: "One hundred twenty-seven modules across nine government function categories. Policy implementation rates improve from thirty-four percent to ninety-four percent.", icon: <Briefcase className="size-5" />, accent: "purple", span: "sm" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Award className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Penta P Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Five pillars. One closed loop.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Perception informs Politics informs Policy informs Policing informs Privacy. The loop never breaks. Forged through years of operating at the highest levels, where conventional approaches fail.
          </p>
        </div>
        <PillarGridBlock
          columns={5}
          variant="numbered"
          pillars={[
            { title: "Perception", body: "Narrative engineering, reputation management, perception proliferation. Ninety-five to ninety-eight percent organic authenticity. Shaping how the world sees a leader, an institution, a nation.", icon: <Sparkles className="size-5" />, accent: "red" },
            { title: "Privacy", body: "Sovereign-grade encryption, zero-trust architecture, post-quantum security. The difference between operational survival and catastrophe. Zero security incidents across fifteen-plus years.", icon: <ShieldCheck className="size-5" />, accent: "purple" },
            { title: "Politics", body: "Electoral intelligence, voter analytics, campaign engineering at the most granular level. Eighty-seven percent booth-level prediction accuracy. Fifteen victories in eighteen engagements.", icon: <Flag className="size-5" />, accent: "cyan" },
            { title: "Policing", body: "Law enforcement intelligence, national security frameworks, threat operations. Real-time threat detection, predictive analytics, and coordinated response.", icon: <ShieldCheck className="size-5" />, accent: "emerald" },
            { title: "Policy", body: "eGovernance transformation, citizen engagement, governance delivery. Policy implementation rates from thirty-four percent to ninety-four percent. Citizen satisfaction from forty-two to eighty-nine percent.", icon: <BookOpen className="size-5" />, accent: "amber-gold" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engineer operational excellence.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Speaking engagements, executive advisory, operational consulting, and sovereign-grade deployments. Every engagement begins with diagnosis. Every outcome is measured.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Brain, accent: "red" },
          { title: "Leadership as Diagnosis", description: "The clinical method applied to organizational leadership. Six principles forged in a delivery room.", href: "/expertise/leadership-as-diagnosis/", icon: Stethoscope, accent: "purple" },
          { title: "Strategic Intelligence", description: "Seeing what others cannot. The intelligence architecture powering every engagement.", href: "/expertise/strategic-intelligence/", icon: Activity, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "emerald" },
        ]}
      />
    </>
  );
}
